(function () {
  "use strict";

  const sectionSelector = ".home-safety-solution";

  function initializeSection(section) {
    if (section.dataset.safetySolutionInitialized === "true") return;

    const leftHouse = section.querySelector(".left-house");
    const rightProducts = section.querySelector(".right-products");

    if (!leftHouse || !rightProducts) return;

    section.dataset.safetySolutionInitialized = "true";

    const areaCache = new Map();
    const productCache = new Map();

    function findTemplate(attribute, value, area) {
      const templates = section.querySelectorAll(`template[${attribute}]`);

      for (const template of templates) {
        if (template.getAttribute(attribute) !== value) continue;
        if (area && template.dataset.productsArea !== area) continue;
        return template;
      }

      return null;
    }

    function takeTemplateNode(template) {
      return template?.content?.firstElementChild || null;
    }

    function setAreaSelection(area, background) {
      section.querySelectorAll(".area-title").forEach((title) => {
        const selected = title.dataset.area === area;
        title.classList.toggle("active", selected);
        title.setAttribute("aria-selected", selected ? "true" : "false");
      });

      if (background) {
        leftHouse.style.backgroundImage = `url("${background}")`;
      }
    }

    function setCollectionSelection(areaInfo, collection) {
      areaInfo.querySelectorAll(".collection-title").forEach((title) => {
        const selected = title.dataset.collection === collection;
        title.classList.toggle("active", selected);
        title.setAttribute("aria-selected", selected ? "true" : "false");
      });
    }

    function setUniformSwiperHeights() {
      const wrappers = section.querySelectorAll(
        ".right-products .collection-swiper .swiper-wrapper"
      );

      if (!wrappers.length) return;

      wrappers.forEach((wrapper) => {
        wrapper.style.height = "auto";
      });

      let maxHeight = 0;
      wrappers.forEach((wrapper) => {
        maxHeight = Math.max(maxHeight, wrapper.scrollHeight);
      });

      wrappers.forEach((wrapper) => {
        wrapper.style.height = `${maxHeight}px`;
      });
    }

    function initializeSwiper(productGrid) {
      const swiperEl = productGrid?.querySelector(".collection-swiper");

      if (!swiperEl) return;

      if (swiperEl.swiper) {
        swiperEl.swiper.update();
        swiperEl.swiper.slideTo(0, 0);
        setUniformSwiperHeights();
        return;
      }

      if (typeof window.Swiper !== "function") return;

      const swiper = new window.Swiper(swiperEl, {
        slidesPerView: 2,
        spaceBetween: 14,
        loop: false,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: swiperEl.querySelector(".swiper-button-next") || undefined,
          prevEl: swiperEl.querySelector(".swiper-button-prev") || undefined,
        },
        pagination: {
          el: swiperEl.querySelector(".swiper-pagination"),
          type: "progressbar",
        },
        breakpoints: {
          1440: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
          1200: {
            slidesPerView: 2,
            spaceBetween: 14,
          },
        },
      });

      const progressEl = swiperEl.querySelector(".swiper-pagination");
      if (progressEl) {
        progressEl.addEventListener("click", (event) => {
          const rect = progressEl.getBoundingClientRect();
          if (!rect.width) return;

          const progress = (event.clientX - rect.left) / rect.width;
          const newIndex = Math.min(
            swiper.slides.length - 1,
            Math.max(0, Math.floor(progress * swiper.slides.length))
          );
          swiper.slideTo(newIndex);
        });
      }

      productGrid.querySelectorAll("img").forEach((image) => {
        if (!image.complete) {
          image.addEventListener("load", setUniformSwiperHeights, { once: true });
        }
      });

      setTimeout(setUniformSwiperHeights, 50);
    }

    function mountArea(area, background) {
      const currentArea = rightProducts.querySelector(".area-info");

      if (currentArea?.dataset.area === area) {
        setAreaSelection(area, background);
        return;
      }

      if (currentArea) {
        areaCache.set(currentArea.dataset.area, currentArea);
        currentArea.remove();
      }

      let nextArea = areaCache.get(area);

      if (!nextArea) {
        nextArea = takeTemplateNode(findTemplate("data-area-template", area));
      }

      if (!nextArea) return;

      rightProducts.appendChild(nextArea);
      setAreaSelection(area, background);

      const firstCollectionTitle = nextArea.querySelector(".collection-title");
      if (firstCollectionTitle) {
        mountProducts(nextArea, firstCollectionTitle.dataset.collection);
      }
    }

    function mountProducts(areaInfo, collection) {
      const area = areaInfo.dataset.area;
      const key = `${area}:${collection}`;
      const host = areaInfo.querySelector(".product-grid-host");
      const currentGrid = host?.querySelector(".product-grid");

      if (!host) return;

      setCollectionSelection(areaInfo, collection);

      if (currentGrid?.dataset.collection === collection) {
        initializeSwiper(currentGrid);
        return;
      }

      if (currentGrid) {
        productCache.set(`${area}:${currentGrid.dataset.collection}`, currentGrid);
        currentGrid.remove();
      }

      let nextGrid = productCache.get(key);

      if (!nextGrid) {
        nextGrid = takeTemplateNode(
          findTemplate("data-products-template", collection, area)
        );
      }

      if (!nextGrid) return;

      host.appendChild(nextGrid);

      requestAnimationFrame(() => {
        initializeSwiper(nextGrid);
        section.dispatchEvent(
          new CustomEvent("home-safety-solution:products-loaded", {
            bubbles: true,
            detail: { area, collection },
          })
        );
      });
    }

    function activate(target) {
      const areaTitle = target.closest(".area-title");

      if (areaTitle && section.contains(areaTitle)) {
        mountArea(areaTitle.dataset.area, areaTitle.dataset.bg);
        return;
      }

      const collectionTitle = target.closest(".collection-title");
      if (!collectionTitle || !section.contains(collectionTitle)) return;

      const areaInfo = collectionTitle.closest(".area-info");
      if (areaInfo) {
        mountProducts(areaInfo, collectionTitle.dataset.collection);
      }
    }

    section.addEventListener("click", (event) => {
      activate(event.target);
    });

    section.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (!event.target.matches(".area-title, .collection-title")) return;

      event.preventDefault();
      activate(event.target);
    });

    const firstAreaTitle = section.querySelector(".area-title");
    if (firstAreaTitle) {
      mountArea(firstAreaTitle.dataset.area, firstAreaTitle.dataset.bg);
    }

    window.addEventListener("load", setUniformSwiperHeights);
    window.addEventListener("resize", setUniformSwiperHeights);
  }

  function initializeAllSections(root) {
    if (root.matches?.(sectionSelector)) initializeSection(root);
    root.querySelectorAll?.(sectionSelector).forEach(initializeSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initializeAllSections(document);
    });
  } else {
    initializeAllSections(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAllSections(event.target);
  });
})();
